import React, { useEffect, useState } from 'react';
import { IconBank, IconOpenBanking } from '@americanexpress/dls-react';

import useAsyncCallback from 'src/hooks/useAsyncCallback';
import { AccountDrawerType, AccountModel } from 'src/types/account.model';
import { InvestmentHoldingModel } from 'src/types/investment-holding.model';
import AccountService from 'src/services/account.service';
import { ManualAccountType } from 'src/types/manualAccount.model';
import ManualAccountService from '../../../../../services/manualAccount.service';
import LiteralsService from 'src/services/literals/LiteralsService';

import ListAccountsViewTemplate from '../listAccountsViewTemplate/ListAccountsViewTemplate';
import Callout from '../../../atoms/callout/Callout';
import WhyShouldYouConnectTooltip from '../../../atoms/tooltips/whyYouShouldConnectTooltip/WhyShouldYouConnectTooltip';
import Typography from 'src/components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import { DLSSize } from 'src/types/dls-size.enum';
import { AddAccountListTemplate } from 'src/types/view-models/listAccounts/listAccountsViewModel.view.model';
import CashAndBankingListAccountsListAccountsViewModel from 'src/types/view-models/listAccounts/cashAndBanking.view.model';
import Selectors from 'src/utils/selectors';

const addAccountListTemplate: AddAccountListTemplate = [
    {
        title: LiteralsService.getLiteral('Onboarding.cashAndBanking.addCheckingTitle'),
        subtitle: LiteralsService.getLiteral('Onboarding.cashAndBanking.addCheckingSubtitle'),
        manualAccountType: ManualAccountType.CheckingAccount
    },
    {
        title: LiteralsService.getLiteral('Onboarding.cashAndBanking.addSavingTitle'),
        subtitle: LiteralsService.getLiteral('Onboarding.cashAndBanking.addSavingSubtitle'),
        manualAccountType: ManualAccountType.SavingAccount
    },
    {
        title: LiteralsService.getLiteral('Onboarding.cashAndBanking.addHysaTitle'),
        subtitle: LiteralsService.getLiteral('Onboarding.cashAndBanking.addHysaSubtitle'),
        manualAccountType: ManualAccountType.HighYieldSavingAccount
    },
    {
        title: LiteralsService.getLiteral('Onboarding.cashAndBanking.otherTitle'),
        manualAccountType: ManualAccountType.OtherCashAndBanking
    }
];

const callout = (
    <Callout
        icon={<IconBank size={DLSSize.MEDIUM} />}
        heading={LiteralsService.getLiteral('Onboarding.cashAndBanking.calloutHeading')}
    >
        <Typography variant={TypographyVariants.BODY}>
            {LiteralsService.getLiteral('Onboarding.cashAndBanking.calloutBody')}
        </Typography>
        <WhyShouldYouConnectTooltip withText />
    </Callout>
);

const formCallout = (
    <Callout
        icon={<IconOpenBanking size={DLSSize.MEDIUM} />}
        heading={LiteralsService.getLiteral('Onboarding.cashAndBanking.formCalloutHeading')}
        body={LiteralsService.getLiteral('Onboarding.cashAndBanking.formCalloutBody')}
    />
);

export default function CashAndBankingListAccountsView({
    user,
    back,
    next,
    handleReloadUser
}: CashAndBankingListAccountsListAccountsViewModel): JSX.Element {
    const [visibleAccounts, setVisibleAccounts] = useState<AccountModel[]>([]);
    const [hiddenAccounts, setHiddenAccounts] = useState<AccountModel[]>([]);
    const [nonDrawerTypeAccounts, setNonDrawerTypeAccounts] = useState<AccountModel[]>();
    const [investmentHoldings, setInvestmentHoldings] = useState<InvestmentHoldingModel[]>([]);
    const [loading, setLoading] = useState(true);

    const refreshAccounts = useAsyncCallback(async (): Promise<void> => {
        const [newVisibleAccounts, newHiddenAccounts] = await AccountService.getCashAndBankingConnectedAccounts(user);
        const nonDrawerTypeAccounts = await AccountService.getNonCashAndBankingConnectedAccounts(user);
        const { newInvestmentHoldings } = await ManualAccountService.getAccounts();
        setVisibleAccounts(newVisibleAccounts);
        setHiddenAccounts(newHiddenAccounts);
        setNonDrawerTypeAccounts(nonDrawerTypeAccounts);
        setInvestmentHoldings(
            newInvestmentHoldings.filter((e) => ManualAccountService.isCashAndBanking({ investmentHolding: e }))
        );
        setLoading(false);
    }, []);

    useEffect(() => {
        void refreshAccounts();
    }, [user.accounts.length]);

    if (loading) return <></>;

    return (
        <ListAccountsViewTemplate
            progressIndex={3}
            accountDrawerType={AccountDrawerType.CashAndBanking}
            title={LiteralsService.getLiteral('Onboarding.cashAndBanking.title')}
            listAccountsHeading={LiteralsService.getLiteral('Onboarding.cashAndBanking.listAccountsHeading')}
            ManualAccountType={ManualAccountService.cashAndBankingAccountTypes}
            visibleAccounts={visibleAccounts}
            hiddenAccounts={hiddenAccounts}
            nonDrawerTypeAccounts={nonDrawerTypeAccounts}
            investmentHoldings={investmentHoldings}
            debts={[]}
            addAccountListTemplate={addAccountListTemplate}
            callout={callout}
            formCallout={formCallout}
            refreshAccounts={refreshAccounts}
            back={back}
            next={next}
            handleReloadUser={handleReloadUser}
            data-cy={Selectors.PlanAhead.CASH_AND_BANKING}
        />
    );
}
